import '../App.css';
import { Outlet } from "react-router-dom";
import { Link } from "react-router-dom";
import { LoginMetamask } from './LoginMetamask';
import { useWallet } from '../hooks/useWallet';
import { useAccess } from '../hooks/useAccess';
import KatbotFooter from './KatbotFooter';
import AppAlerts from './AppAlerts';

const katbotLogo = new URL('../assets/logo64.png', import.meta.url).href;

export const HeaderBar = ({config}) => {
  const { jwtToken } = useWallet();
  // Hide the way in once the product is closed (KAT-230). LoginMetamask
  // self-gates, so a signed-in user keeps their disconnect control.
  const { product } = useAccess();

  return (
    <header>
      <nav className="bg-black text-white px-4 py-3">
        <div className="mx-auto max-w-7xl">
          {/* Mobile Layout: flex-col, centered */}
          <div className="flex flex-col items-center justify-center gap-4 md:hidden">
            {/* Site Title - Mobile */}
            <div className="flex items-center gap-2">
              <Link to="/" className="flex items-center gap-2 no-underline hover:no-underline text-white">
                <img src={katbotLogo} alt="Katbot Logo" className="w-10 h-10" />
                <span className="text-xl font-semibold">Katbot.ai</span>
              </Link>
            </div>
            
            {/* Agent Key Display - Mobile
            <div className="text-xs text-gray-300 text-center">
              Agent: <span className="font-mono">{displayKey}</span>
            </div> */}
            
            {/* Navigation Links - Mobile */}
            <div className="flex flex-col items-center gap-3 w-full">
              <ul className="flex items-center justify-center gap-6 text-center">
                <li>
                  <Link to={config.docsUrl} className="hover:text-gray-400 underline text-white text-sm" target='_blank'>
                    Docs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/licensing"
                    className="hover:text-gray-400 underline text-white text-sm"
                  >
                    Licensing
                  </Link>
                </li>
                <li>
                  {/* Plain anchor, not a Link: a full navigation lets the browser
                      handle the #contact hash without any scroll-restore JS. */}
                  <a
                    href="/licensing#contact"
                    className="rounded-lg bg-cyan-400 px-3 py-1.5 text-sm font-semibold text-gray-900 no-underline hover:bg-cyan-300"
                  >
                    Email us
                  </a>
                </li>
                {jwtToken && product && (
                  <li>
                    <Link
                      to="/dashboard"
                      className="hover:text-gray-400 underline text-white text-sm"
                    >
                      Dashboard
                    </Link>
                  </li>
                )}
              </ul>
              
              {/* Login Component - Mobile */}
              <div className="w-full max-w-sm">
                <LoginMetamask />
              </div>
            </div>
          </div>

          {/* Desktop Layout: flex-row, space-between */}
          <div className="hidden md:flex flex-row items-center justify-between">
            {/* Site Title - Desktop */}
            <Link to="/" className="flex items-center gap-2 no-underline hover:no-underline text-white">
              <img src={katbotLogo} alt="Katbot Logo" className="w-12 h-12" />
              <span className="text-2xl font-semibold">Katbot.ai</span>
            </Link>
            
            {/* Navigation and Login - Desktop */}
            <div className="flex flex-row items-center gap-6">
              {/* Site Navigation - Desktop */}
              <ul className="flex items-center gap-6">
                <li>
                  <Link
                    to={config.docsUrl}
                    className="hover:text-gray-400 underline text-white transition-colors duration-200"
                    target='_blank'
                  >
                    Docs
                  </Link>
                </li>
                <li>
                  <Link
                    to="/licensing"
                    className="hover:text-gray-400 underline text-white transition-colors duration-200"
                  >
                    Licensing
                  </Link>
                </li>
                <li>
                  {/* Plain anchor, not a Link: a full navigation lets the browser
                      handle the #contact hash without any scroll-restore JS. */}
                  <a
                    href="/licensing#contact"
                    className="rounded-lg bg-cyan-400 px-4 py-2 text-sm font-semibold text-gray-900 no-underline transition-colors duration-200 hover:bg-cyan-300"
                  >
                    Email us
                  </a>
                </li>
                {jwtToken && product && (
                  <li>
                    <Link
                      to="/dashboard"
                      className="hover:text-gray-400 underline text-white transition-colors duration-200"
                    >
                      Dashboard
                    </Link>
                  </li>
                )}
              </ul>
              
              {/* Login Component - Desktop */}
              <div className="w-80 [&>div]:mb-0">
                <LoginMetamask />
                {jwtToken && product && (<AppAlerts />)}
              </div>
            </div>
          </div>
        </div>
      </nav>
    </header>
  );
}

export const FooterBar = ({config}) => {
  return (
    <div className="bg-gray-900 text-white">
      <KatbotFooter config={config} />
    </div>
  );
}


export const PageLayout = ({ children, config }) => {
  return (
    <div className="flex flex-col min-h-screen">
      {/* Header */}
      <HeaderBar config={config} />
      
      {/* Main Content (Flexible Space) */}
      <main className="flex-1 page-children">
        <Outlet />
      </main>
      
      {/* Footer */}
      <FooterBar config={config} />
    </div>
  );
}